/**
 * persistOnboardingData.ts
 * Persist transformed onboarding data to IndexedDB
 *
 * Responsibilities:
 * - Save all onboarding entities in a single atomic Dexie transaction
 * - Handle errors and provide meaningful feedback
 * - Return success/failure status with created entity IDs
 */

import { db } from '@/lib/db';
import { transformOnboardingData } from './onboardingTransforms';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import { getSyncOperationService } from '@/services/sync/SyncOperationService';
import { markAsInstrumentedWriteContext } from '@/services/sync/SyncImportContext';
import { buildSetOps } from '@/services/sync/OperationBuilder';

/**
 * Result of persisting onboarding data
 */
export interface PersistOnboardingResult {
  success: boolean;
  error?: string;
  entityIds?: {
    basicSettingsId: string;
    strategyId: string;
    accountId: string;
    brokerId: string;
    playbookIds: string[];
    entryModelIds: string[];
    criteriaItemIds: string[];
    confluenceIds: string[];
    marketConditionCategoryIds: string[];
    entryModelCategoryIds: string[];
    balanceHistoryId: string;
    accountRiskId?: string;
  };
}

/**
 * Persist all onboarding data to IndexedDB in a single atomic transaction
 *
 * @param draft - Complete onboarding draft data
 * @returns Result object with success status and entity IDs
 */
export async function persistOnboardingData(
  draft: OnboardingDraft
): Promise<PersistOnboardingResult> {
  try {
    // Transform draft data to schema entities
    const entities = transformOnboardingData(draft);

    // Perform atomic transaction - all or nothing
    // Includes sync tables so Dexie can join nested logBatch() transactions
    await db.transaction(
      'rw',
      [
        db.basic_settings,
        db.strategies,
        db.playbooks,
        db.entry_models,
        db.entry_model_categories,
        db.playbook_criteria_items,
        db.confluences,
        db.market_condition_categories,
        db.brokers,
        db.accounts,
        db.balance_histories,
        db.account_risks,
        db.sync_operations,
        db.sync_state
      ],
      async () => {
        markAsInstrumentedWriteContext(); // Suppress safety-net hook warnings
        // Save in dependency order to maintain referential integrity
        // Each write is followed by op logging (per-record batching)
        const syncService = getSyncOperationService();

        // 1. Basic Settings (no dependencies)
        await db.basic_settings.add(entities.basicSettings);
        const basicSettingsOps = buildSetOps(
          'basic_settings',
          entities.basicSettings.id,
          entities.basicSettings as unknown as Record<string, unknown>
        );
        await syncService.logBatch(basicSettingsOps);

        // 2. Categories (no dependencies)
        await db.market_condition_categories.bulkAdd(
          entities.marketConditionCategories
        );
        const marketConditionCategoryOps = entities.marketConditionCategories.flatMap(
          category =>
            buildSetOps(
              'market_condition_categories',
              category.id,
              category as unknown as Record<string, unknown>
            )
        );
        if (marketConditionCategoryOps.length > 0) {
          await syncService.logBatch(marketConditionCategoryOps);
        }

        await db.entry_model_categories.bulkAdd(entities.entryModelCategories);
        const entryModelCategoryOps = entities.entryModelCategories.flatMap(
          category =>
            buildSetOps(
              'entry_model_categories',
              category.id,
              category as unknown as Record<string, unknown>
            )
        );
        if (entryModelCategoryOps.length > 0) {
          await syncService.logBatch(entryModelCategoryOps);
        }

        // 3. Items (depend on categories)
        await db.playbook_criteria_items.bulkAdd(entities.criteriaItems);
        const criteriaItemOps = entities.criteriaItems.flatMap(item =>
          buildSetOps(
            'playbook_criteria_items',
            item.id,
            item as unknown as Record<string, unknown>
          )
        );
        if (criteriaItemOps.length > 0) {
          await syncService.logBatch(criteriaItemOps);
        }

        await db.confluences.bulkAdd(entities.confluences);
        const confluenceOps = entities.confluences.flatMap(
          confluence =>
            buildSetOps(
              'confluences',
              confluence.id,
              confluence as unknown as Record<string, unknown>
            )
        );
        if (confluenceOps.length > 0) {
          await syncService.logBatch(confluenceOps);
        }

        await db.entry_models.bulkAdd(entities.entryModels);
        const entryModelOps = entities.entryModels.flatMap(model =>
          buildSetOps(
            'entry_models',
            model.id,
            model as unknown as Record<string, unknown>
          )
        );
        if (entryModelOps.length > 0) {
          await syncService.logBatch(entryModelOps);
        }

        // 4. Playbooks (depend on items)
        await db.playbooks.bulkAdd(entities.playbooks);
        const playbookOps = entities.playbooks.flatMap(playbook =>
          buildSetOps(
            'playbooks',
            playbook.id,
            playbook as unknown as Record<string, unknown>
          )
        );
        if (playbookOps.length > 0) {
          await syncService.logBatch(playbookOps);
        }

        // 5. Strategy (depends on playbooks)
        await db.strategies.add(entities.strategy);
        const strategyOps = buildSetOps(
          'strategies',
          entities.strategy.id,
          entities.strategy as unknown as Record<string, unknown>
        );
        await syncService.logBatch(strategyOps);

        // 6. Broker (no dependencies)
        await db.brokers.add(entities.broker);
        const brokerOps = buildSetOps(
          'brokers',
          entities.broker.id,
          entities.broker as unknown as Record<string, unknown>
        );
        await syncService.logBatch(brokerOps);

        // 7. Account (depends on broker)
        await db.accounts.add(entities.account);
        const accountOps = buildSetOps(
          'accounts',
          entities.account.id,
          entities.account as unknown as Record<string, unknown>
        );
        await syncService.logBatch(accountOps);

        // 8. Balance history (depends on account)
        await db.balance_histories.add(entities.balanceHistory);
        const balanceHistoryOps = buildSetOps(
          'balance_histories',
          entities.balanceHistory.id,
          entities.balanceHistory as unknown as Record<string, unknown>
        );
        await syncService.logBatch(balanceHistoryOps);

        // 9. Account risk (depends on account, strategy, playbooks) - optional
        if (entities.accountRisk) {
          await db.account_risks.add(entities.accountRisk);
          const accountRiskOps = buildSetOps(
            'account_risks',
            entities.accountRisk.id,
            entities.accountRisk as unknown as Record<string, unknown>
          );
          await syncService.logBatch(accountRiskOps);
        }
      }
    );

    // Return success with all created entity IDs
    const entityIds: NonNullable<PersistOnboardingResult['entityIds']> = {
      basicSettingsId: entities.basicSettings.id,
      strategyId: entities.strategy.id,
      accountId: entities.account.id,
      brokerId: entities.broker.id,
      playbookIds: entities.playbooks.map(p => p.id),
      entryModelIds: entities.entryModels.map(e => e.id),
      criteriaItemIds: entities.criteriaItems.map(c => c.id),
      confluenceIds: entities.confluences.map(c => c.id),
      marketConditionCategoryIds: entities.marketConditionCategories.map(
        m => m.id
      ),
      entryModelCategoryIds: entities.entryModelCategories.map(e => e.id),
      balanceHistoryId: entities.balanceHistory.id
    };

    if (entities.accountRisk) {
      entityIds.accountRiskId = entities.accountRisk.id;
    }

    return {
      success: true,
      entityIds
    };
  } catch (error) {
    console.error('Failed to persist onboarding data:', error);

    // Return failure with error message
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Unknown error occurred while saving data'
    };
  }
}

/**
 * Verify onboarding data was persisted correctly
 *
 * @param entityIds - Entity IDs returned from persistOnboardingData
 * @returns True if all entities exist in database
 */
export async function verifyOnboardingPersistence(
  entityIds: NonNullable<PersistOnboardingResult['entityIds']>
): Promise<boolean> {
  try {
    const [
      basicSettings,
      strategy,
      account,
      broker,
      playbooks,
      entryModels,
      criteriaItems,
      confluences,
      marketConditionCategories,
      entryModelCategories,
      balanceHistory
    ] = await Promise.all([
      db.basic_settings.get(entityIds.basicSettingsId),
      db.strategies.get(entityIds.strategyId),
      db.accounts.get(entityIds.accountId),
      db.brokers.get(entityIds.brokerId),
      db.playbooks.where('id').anyOf(entityIds.playbookIds).toArray(),
      db.entry_models.where('id').anyOf(entityIds.entryModelIds).toArray(),
      db.playbook_criteria_items
        .where('id')
        .anyOf(entityIds.criteriaItemIds)
        .toArray(),
      db.confluences
        .where('id')
        .anyOf(entityIds.confluenceIds)
        .toArray(),
      db.market_condition_categories
        .where('id')
        .anyOf(entityIds.marketConditionCategoryIds)
        .toArray(),
      db.entry_model_categories
        .where('id')
        .anyOf(entityIds.entryModelCategoryIds)
        .toArray(),
      db.balance_histories.get(entityIds.balanceHistoryId)
    ]);

    // Verify all entities exist and counts match
    return !!(
      basicSettings &&
      strategy &&
      account &&
      broker &&
      balanceHistory &&
      playbooks.length === entityIds.playbookIds.length &&
      entryModels.length === entityIds.entryModelIds.length &&
      criteriaItems.length === entityIds.criteriaItemIds.length &&
      confluences.length === entityIds.confluenceIds.length &&
      marketConditionCategories.length ===
        entityIds.marketConditionCategoryIds.length &&
      entryModelCategories.length === entityIds.entryModelCategoryIds.length
    );
  } catch (error) {
    console.error('Failed to verify onboarding persistence:', error);
    return false;
  }
}
